import { Alert, Box } from "@mui/material";
import { useSyncExternalStore } from "react";
import ChatHeader from "../../../components/layout/ChatHeader";
import { useThemeContext } from "../../../globalContext/global-context";
import { hostThemes } from "../../../theme/hostThemes";
import {
  SurveyAttemptContent,
  type SurveyHostProps,
} from "../shared/HostShared";
import { preferredResponseFlow } from "../shared/responseFlow";
import { ChatCompletionLayout } from "./ChatCompletionLayout";
import { ChatChoiceFooter, ChatQuestionHost } from "./ChatQuestionHost";
import {
  CompletionTranscript,
  HistoricalEntry,
  shouldShowGroupHeading,
} from "./ChatTranscript";

export const ChatSurveyHost = ({
  session,
  progress,
  arrangement,
  capabilities,
  view,
  onInspect,
  onRedirect,
  draftState,
  setDraftState,
}: SurveyHostProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const colors = theme.tokens.color;
  const snapshot = useSyncExternalStore(session.subscribe, session.getSnapshot);
  const attempt = snapshot.attempt;
  const completionEntries =
    attempt?.question.question.type === "complete"
      ? snapshot.transcript.filter(
          (entry) =>
            entry.kind === "question" && entry.attemptId === attempt.id,
        )
      : snapshot.transcript;
  const activeQuestion =
    attempt &&
    attempt.question.question.type !== "complete" &&
    attempt.question.question.type !== "webview_modal";

  return (
    <Box
      data-testid="session-host-chat"
      sx={{
        height: "100%",
        width: "100%",
        boxSizing: "border-box",
        maxWidth: hostThemes.chat.shell.maxWidth,
        margin: "0 auto",
        padding: { xs: 0, sm: `${hostThemes.chat.shell.desktopInset}px` },
      }}
    >
      <Box
        sx={{
          width: "100%",
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          // Prevent scrollIntoView from scrolling the shell around its footer.
          overflow: "clip",
          position: "relative",
          borderRadius: {
            xs: 0,
            sm: `${hostThemes.chat.shell.cornerRadius}px`,
          },
          boxShadow: theme.tokens.elevation["3"],
          backgroundColor: colors.surface.canvas,
          backgroundImage: `url(${new URL(theme.assets.chat.background, globalThis.location.origin).href})`,
          color: colors.text.primary,
        }}
      >
        <ChatHeader timerImageOPen={false} progress={progress} />
        {(!attempt || attempt.question.question.type === "complete") && (
          <CompletionTranscript
            attemptId={attempt?.id}
            entries={completionEntries}
          />
        )}
        {snapshot.error && (
          <Alert severity="error">{snapshot.error.message}</Alert>
        )}
        <SurveyAttemptContent
          session={session}
          snapshot={snapshot}
          Frame={ChatQuestionHost}
          preferredResponseFlow={preferredResponseFlow("chat")}
          completionLayout={ChatCompletionLayout}
          arrangement={arrangement}
          capabilities={capabilities}
          view={view}
          onInspect={onInspect}
          onRedirect={onRedirect}
          draftState={draftState}
          setDraftState={setDraftState}
          promptInHistory
          history={snapshot.transcript.map((entry, index) => (
            <HistoricalEntry
              key={`${entry.attemptId}-${entry.kind}-${index}`}
              entry={entry}
              showGroupHeading={shouldShowGroupHeading(
                snapshot.transcript,
                index,
              )}
            />
          ))}
        />
        {!activeQuestion && (
          <ChatChoiceFooter
            controls={{ canSubmit: false }}
            hintKey="multichoice"
          />
        )}
      </Box>
    </Box>
  );
};
